import React, { useState, useRef } from 'react';

function DragDropUpload({
  label,
  required,
  accept,
  infoText,
  subInfoText,
  value,
  onChange,
  onValidationError
}) {
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const validateFile = (file) => {
    if (!file) return false;

    // Validate type
    if (accept === '.mp3') {
      if (!file.name.toLowerCase().endsWith('.mp3') && !file.type.includes('audio')) {
        onValidationError?.(`${label} must be an MP3 file.`);
        return false;
      }
    } else if (accept.includes('image')) {
      const allowedExtensions = ['.png', '.jpg', '.jpeg'];
      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedExtensions.includes(fileExt) && !file.type.includes('image')) {
        onValidationError?.(`${label} must be PNG or JPG only.`);
        return false;
      }
      
      // Validate size (5MB for image)
      if (file.size > 5 * 1024 * 1024) {
        onValidationError?.(`${label} must be smaller than 5MB.`);
        return false;
      }
    }

    onValidationError?.(null);
    return true;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        handleFileSelect(file);
      }
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        handleFileSelect(file);
      }
    }
  };

  const handleFileSelect = (file) => {
    if (accept.includes('image')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onChange({
          file: file,
          name: file.name,
          size: file.size,
          previewUrl: event.target.result
        });
      };
      reader.readAsDataURL(file);
    } else {
      onChange({
        file: file,
        name: file.name,
        size: file.size,
        previewUrl: null
      });
    }
  };

  const handleButtonClick = () => {
    inputRef.current.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="upload-container">
      <label className="form-field-label">
        {label} {required && <span className="required-asterisk">*</span>}
      </label>
      
      <div 
        className={`upload-drop-zone ${isDragActive ? 'drag-active' : ''} ${value ? 'has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <input 
          ref={inputRef}
          type="file" 
          className="upload-file-input" 
          accept={accept}
          onChange={handleChange}
          style={{ display: 'none' }}
        />

        {!value ? (
          <div className="upload-content-wrapper" onClick={handleButtonClick}>
            <div className="upload-icon-circle">
              {/* Cloud Upload Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            
            <p className="upload-info-text">
              {infoText}
            </p>
            {subInfoText && <p className="upload-sub-info-text">{subInfoText}</p>}
            
            <button type="button" className="upload-action-btn">
              Upload File
            </button>
          </div>
        ) : (
          <div className="upload-preview-wrapper">
            {accept.includes('image') && value.previewUrl ? (
              <div className="image-preview-container">
                <img src={value.previewUrl} alt="Song Art Preview" className="image-preview-thumbnail" />
                <div className="image-preview-details">
                  <span className="file-name">{value.name}</span>
                  <span className="file-size">{formatFileSize(value.size)}</span>
                </div>
              </div>
            ) : (
              <div className="audio-preview-container">
                <div className="audio-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18V5l12-2v13" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="18" cy="16" r="3" />
                  </svg>
                </div>
                <div className="audio-preview-details">
                  <span className="file-name">{value.name}</span>
                  <span className="file-size">{formatFileSize(value.size)}</span>
                </div>
              </div>
            )}
            
            <button 
              type="button" 
              className="remove-file-btn" 
              onClick={() => onChange(null)}
              aria-label="Remove uploaded file"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DragDropUpload;
